import { createContext, ReactNode, useContext } from 'react';
import { useAuth as useClerkAuth, useUser as useClerkUser } from '@clerk/clerk-react';
import { config } from '../../utils/config';
import { ClerkProvider } from '../ClerkProvider';
import { ClerkRolesEnum } from '../ClerkProvider/types/clerk-roles.enum';
import { NewSignupPageSectionEnum } from 'pages/SignupPage/enums';

export interface AuthContextValue {
  isLoaded: boolean;
  isSignedIn: boolean;
  getToken: () => Promise<string | null>;
  signOut: () => void | Promise<void>;
  orgRole?: string | null;
  email?: string;
  currentSignUpStep: NewSignupPageSectionEnum | null;
  updateUser?:
    | NonNullable<ReturnType<typeof useClerkUser>['user']>['update']
    | ((payload: unknown) => void | Promise<void>);
}

const MOCK_AUTH: AuthContextValue = {
  isLoaded: true,
  isSignedIn: true,
  getToken: () => Promise.resolve(null),
  signOut: () => Promise.resolve(),
  orgRole: ClerkRolesEnum.Member,
  email: 'localhost@strideup.co',
  currentSignUpStep: NewSignupPageSectionEnum.COMPLETED,
  updateUser: () => Promise.resolve(),
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);

  if (value === null) {
    throw new Error('useAuth must be used within AuthProvider');
  }

  return value;
}

function ClerkAuthBridge({ children }: { children: ReactNode }) {
  const clerkAuth = useClerkAuth();
  const { user } = useClerkUser();

  const value: AuthContextValue = {
    isLoaded: Boolean(clerkAuth.isLoaded),
    isSignedIn: Boolean(clerkAuth.isSignedIn),
    getToken: clerkAuth.getToken,
    signOut: clerkAuth.signOut,
    orgRole: clerkAuth.orgRole,
    email: user?.primaryEmailAddress?.emailAddress,
    currentSignUpStep: user?.unsafeMetadata?.currentSignUpStep
      ? (user?.unsafeMetadata?.currentSignUpStep as NewSignupPageSectionEnum)
      : null,
    updateUser: user?.update,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  if (config.isLocalAuth) {
    return <AuthContext.Provider value={MOCK_AUTH}>{children}</AuthContext.Provider>;
  }

  return (
    <ClerkProvider>
      <ClerkAuthBridge>{children}</ClerkAuthBridge>
    </ClerkProvider>
  );
}
